import { useReduxState } from 'hooks';
import { Button } from 'components/common';
import {
  Checkbox,
  ColorPicker,
  FormGroup,
  FormSection,
  LanguageVariants,
  RichTextOrHtml,
  Text
} from 'components/common/form';
import { SiteSettingsForm } from '.';

const SiteSettingsLoginPage = () => {
  const {
    messages,
    serverConfig: {
      authLocalLoginButton,
      authLocalLoginSubmitButton,
      authLocalLoginUseUsernameLabel,
      courseLoginUseLoginBackgroundImage,
      joinPageLoginCustomContent,
      joinPageLoginCustomContentBeside,
      joinPageLoginCustomContentLocales,
      joinPageLoginCustomContentRegisterEnabled,
      localLoginCustomContent,
      localLoginCustomContentBeside,
      localLoginCustomContentLocales,
      loginCustomContent,
      loginCustomContentBeside,
      loginCustomContentLocales,
      loginPageGuestLoginButton,
      loginPageRegisterButton,
      loginPageRegisterLinkAsButton,
      loginTitle,
      loginTitleLocales,
      pathLoginUseLoginBackgroundImage
    },
    theme
  } = useReduxState();

  const initialValues = {
    authLocalLoginButton,
    authLocalLoginSubmitButton,
    authLocalLoginUseUsernameLabel,
    courseLoginUseLoginBackgroundImage,
    joinPageLoginCustomContent,
    joinPageLoginCustomContentBeside,
    joinPageLoginCustomContentLocales,
    joinPageLoginCustomContentRegisterEnabled,
    localLoginCustomContent,
    localLoginCustomContentBeside,
    localLoginCustomContentLocales,
    loginCustomContent,
    loginCustomContentBeside,
    loginCustomContentLocales,
    loginPageGuestLoginButton,
    loginPageRegisterButton,
    loginPageRegisterLinkAsButton,
    loginTitle,
    loginTitleLocales,
    pathLoginUseLoginBackgroundImage
  };

  return (
    <SiteSettingsForm
      initialValues={initialValues}
      padding={0}
      title="Login page"
    >
      {(formState) => (
        <div>
          <FormSection>
            <FormGroup label="Login title">
              <Text
                field="loginTitle"
                id="login-title"
                placeholder={messages.authSignIn}
              />

              <LanguageVariants field="loginTitleLocales" type="text" />

              <FormGroup label="Local login identifier label">
                <Checkbox
                  field="authLocalLoginUseUsernameLabel"
                  id="auth-local-login-use-username-label"
                  label="Use username label for identifier"
                />
              </FormGroup>
            </FormGroup>

            <FormGroup label="Custom login content" style={{ marginTop: 50 }}>
              <RichTextOrHtml
                field="loginCustomContent"
                id="login-custom-content"
              />

              <LanguageVariants
                field="loginCustomContentLocales"
                type="richtextorhtml"
              />
            </FormGroup>

            <FormGroup>
              <Checkbox
                field="loginCustomContentBeside"
                id="login-custom-content-beside"
                label="Show custom content beside login options"
              />
            </FormGroup>

            <FormGroup
              label={messages.siteSettingsLocalLoginCustom}
              style={{ marginTop: 50 }}
            >
              <RichTextOrHtml
                field="localLoginCustomContent"
                id="local-login-custom-content"
              />

              <LanguageVariants
                field="localLoginCustomContentLocales"
                type="richtextorhtml"
              />
            </FormGroup>

            <FormGroup>
              <Checkbox
                field="localLoginCustomContentBeside"
                id="local-login-custom-content-beside"
                label={messages.siteSettingsLocalLoginCustomBeside}
              />
            </FormGroup>

            <FormGroup
              label="Custom join page login content"
              style={{ marginTop: 50 }}
            >
              <RichTextOrHtml
                field="joinPageLoginCustomContent"
                id="join-page-login-custom-content"
              />

              <LanguageVariants
                field="joinPageLoginCustomContentLocales"
                type="richtextorhtml"
              />
            </FormGroup>

            <FormGroup>
              <Checkbox
                field="joinPageLoginCustomContentBeside"
                id="join-page-login-custom-content-beside"
                label="Show custom content beside join page login options"
                style={{ marginBottom: 5 }}
              />
              <Checkbox
                field="joinPageLoginCustomContentRegisterEnabled"
                id="join-page-login-custom-content-register-enabled"
                label="Only show custom content if registration is enabled"
              />
            </FormGroup>

            <FormGroup
              label="Login page register link"
              style={{ marginTop: 50 }}
            >
              <Checkbox
                field="loginPageRegisterLinkAsButton"
                id="login-page-register-link-as-button"
                label="Show register link as button"
              />
            </FormGroup>
          </FormSection>

          <FormSection title="Course login page">
            <FormGroup>
              <Checkbox
                field="courseLoginUseLoginBackgroundImage"
                id="course-login-use-login-background-image"
                label="Use login background image instead of course picture"
              />
            </FormGroup>
          </FormSection>

          <FormSection title="Path login page">
            <FormGroup>
              <Checkbox
                field="pathLoginUseLoginBackgroundImage"
                id="path-login-use-login-background-image"
                label="Use login background image instead of path picture"
              />
            </FormGroup>
          </FormSection>

          <FormSection title="Buttons" maxWidth="100%">
            <div className="button-section">
              <div className="button-section-title">Local account</div>

              <div className="row-sm">
                <div className="col-min">
                  <FormGroup label="Text color">
                    <ColorPicker
                      field="authLocalLoginButton.color"
                      style={{ width: 160 }}
                    />
                  </FormGroup>
                </div>
                <div className="col-min">
                  <FormGroup label="Background color">
                    <ColorPicker
                      field="authLocalLoginButton.backgroundColor"
                      style={{ width: 160 }}
                    />
                  </FormGroup>
                </div>
                <div className="col-min">
                  <FormGroup label="Background hover color">
                    <ColorPicker
                      field="authLocalLoginButton.backgroundColorHover"
                      style={{ width: 180 }}
                    />
                  </FormGroup>
                </div>
                <div className="col">
                  <div style={{ padding: '27px 15px 0' }}>
                    <div>
                      <Button
                        category="light"
                        customColor={
                          formState.values.authLocalLoginButton?.color
                        }
                        customBackgroundColor={
                          formState.values.authLocalLoginButton?.backgroundColor
                        }
                        customBackgroundColorHover={
                          formState.values.authLocalLoginButton
                            ?.backgroundColorHover
                        }
                        style={{ width: '100%' }}
                        text={
                          formState.values.authLocalLoginButton?.text ||
                          messages.authVuoAccount
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row-md" style={{ marginTop: 30 }}>
                <div className="col-1of2">
                  <FormGroup label="Button text">
                    <Text
                      field="authLocalLoginButton.text"
                      placeholder={messages.authVuoAccount}
                      style={{ maxWidth: 270 }}
                    />

                    <LanguageVariants
                      field="authLocalLoginButton.textLocales"
                      type="text"
                    />
                  </FormGroup>
                </div>

                <div className="col-1of2">
                  <FormGroup label="Create account button text">
                    <Text
                      field="authLocalLoginButton.registerText"
                      placeholder={messages.authCreateVuoAccount}
                      style={{ maxWidth: 270 }}
                    />

                    <LanguageVariants
                      field="authLocalLoginButton.registerTextLocales"
                      type="text"
                    />
                  </FormGroup>
                </div>
              </div>
            </div>

            <div className="button-section">
              <div className="button-section-title">Submit button</div>

              <div className="row-sm">
                <div className="col-min">
                  <FormGroup label="Text color">
                    <ColorPicker
                      field="authLocalLoginSubmitButton.color"
                      style={{ width: 160 }}
                    />
                  </FormGroup>
                </div>
                <div className="col-min">
                  <FormGroup label="Background color">
                    <ColorPicker
                      field="authLocalLoginSubmitButton.backgroundColor"
                      style={{ width: 160 }}
                    />
                  </FormGroup>
                </div>
                <div className="col-min">
                  <FormGroup label="Background hover color">
                    <ColorPicker
                      field="authLocalLoginSubmitButton.backgroundColorHover"
                      style={{ width: 180 }}
                    />
                  </FormGroup>
                </div>
                <div className="col">
                  <div style={{ padding: '27px 15px 0' }}>
                    <div>
                      <Button
                        category="success"
                        customColor={
                          formState.values.authLocalLoginSubmitButton?.color
                        }
                        customBackgroundColor={
                          formState.values.authLocalLoginSubmitButton
                            ?.backgroundColor
                        }
                        customBackgroundColorHover={
                          formState.values.authLocalLoginSubmitButton
                            ?.backgroundColorHover
                        }
                        style={{ width: '100%' }}
                        text={messages.authSignIn}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="button-section">
              <div className="button-section-title">Register button</div>

              <div className="row-sm">
                <div className="col-min">
                  <FormGroup label="Text color">
                    <ColorPicker
                      field="loginPageRegisterButton.color"
                      style={{ width: 160 }}
                    />
                  </FormGroup>
                </div>
                <div className="col-min">
                  <FormGroup label="Background color">
                    <ColorPicker
                      field="loginPageRegisterButton.backgroundColor"
                      style={{ width: 160 }}
                    />
                  </FormGroup>
                </div>
                <div className="col-min">
                  <FormGroup label="Background hover color">
                    <ColorPicker
                      field="loginPageRegisterButton.backgroundColorHover"
                      style={{ width: 180 }}
                    />
                  </FormGroup>
                </div>
                <div className="col">
                  <div style={{ padding: '27px 15px 0' }}>
                    <div>
                      <Button
                        category="light"
                        customColor={
                          formState.values.loginPageRegisterButton?.color
                        }
                        customBackgroundColor={
                          formState.values.loginPageRegisterButton
                            ?.backgroundColor
                        }
                        customBackgroundColorHover={
                          formState.values.loginPageRegisterButton
                            ?.backgroundColorHover
                        }
                        style={{ width: '100%' }}
                        text={
                          formState.values.loginPageRegisterButton?.text ||
                          messages.authRegister
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row-md" style={{ marginTop: 30 }}>
                <div className="col-1of2">
                  <FormGroup label="Button text">
                    <Text
                      field="loginPageRegisterButton.text"
                      placeholder={messages.authRegister}
                      style={{ maxWidth: 270 }}
                    />

                    <LanguageVariants
                      field="loginPageRegisterButton.textLocales"
                      type="text"
                    />
                  </FormGroup>
                </div>

                <div className="col-1of2">
                  <FormGroup label="Position">
                    <Checkbox
                      field="loginPageRegisterButton.positionAtTop"
                      id="login-page-register-button-position-at-top"
                      label="Position at top"
                    />
                  </FormGroup>
                </div>
              </div>
            </div>

            <div className="button-section">
              <div className="button-section-title">Guest login button</div>

              <div className="row-sm">
                <div className="col-min">
                  <FormGroup label="Text color">
                    <ColorPicker
                      field="loginPageGuestLoginButton.color"
                      style={{ width: 160 }}
                    />
                  </FormGroup>
                </div>
                <div className="col-min">
                  <FormGroup label="Background color">
                    <ColorPicker
                      field="loginPageGuestLoginButton.backgroundColor"
                      style={{ width: 160 }}
                    />
                  </FormGroup>
                </div>
                <div className="col-min">
                  <FormGroup label="Background hover color">
                    <ColorPicker
                      field="loginPageGuestLoginButton.backgroundColorHover"
                      style={{ width: 180 }}
                    />
                  </FormGroup>
                </div>
                <div className="col">
                  <div style={{ padding: '27px 15px 0' }}>
                    <div>
                      <Button
                        category="light"
                        customColor={
                          formState.values.loginPageGuestLoginButton?.color
                        }
                        customBackgroundColor={
                          formState.values.loginPageGuestLoginButton
                            ?.backgroundColor
                        }
                        customBackgroundColorHover={
                          formState.values.loginPageGuestLoginButton
                            ?.backgroundColorHover
                        }
                        style={{ width: '100%' }}
                        text={
                          formState.values.loginPageGuestLoginButton?.text ||
                          messages.loginGuestSignIn
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row-md" style={{ marginTop: 30 }}>
                <div className="col-1of2">
                  <FormGroup label="Button text">
                    <Text
                      field="loginPageGuestLoginButton.text"
                      placeholder={messages.loginGuestSignIn}
                      style={{ maxWidth: 270 }}
                    />

                    <LanguageVariants
                      field="loginPageGuestLoginButton.textLocales"
                      type="text"
                    />
                  </FormGroup>
                </div>

                <div className="col-1of2">
                  <FormGroup label="Position">
                    <Checkbox
                      field="loginPageGuestLoginButton.positionAtTop"
                      id="login-page-guest-login-button-position-at-top"
                      label="Position at top"
                    />
                  </FormGroup>
                </div>
              </div>
            </div>
          </FormSection>

          <style jsx>{`
            .button-section {
              border: 1px solid ${theme.lightGreyDark};
              border-radius: ${theme.cardBorderRadius};
              margin-top: 25px;
              padding: 15px;
            }
            .button-section-title {
              color: ${theme.textColorLighter};
              font-size: 1.4rem;
              font-weight: 600;
              margin-bottom: 10px;
            }
          `}</style>
        </div>
      )}
    </SiteSettingsForm>
  );
};

export default SiteSettingsLoginPage;
